import { useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { de } from '@holymoly/i18n';
import { PageTitle } from '@/components/PageTitle';
import { formatDateTime } from '@/lib/format';
import { requirePermission } from '@/lib/guards';
import { orpc } from '@/lib/orpc';

export const Route = createFileRoute('/_app/admin/audit')({
  validateSearch: z.object({ action: z.string().optional() }),
  beforeLoad: ({ context }) => requirePermission(context.me, 'audit.view'),
  component: AuditPage
});

const knownActions = Object.keys(de.auditActions);

function AuditPage() {
  const { t } = useTranslation();
  const { action } = Route.useSearch();
  const navigate = Route.useNavigate();
  const log = useInfiniteQuery(
    orpc.audit.list.infiniteOptions({
      input: (before: string | undefined) => ({ limit: 50, ...(before && { before }), ...(action && { action }) }),
      initialPageParam: undefined,
      getNextPageParam: (last) => last.nextCursor ?? undefined
    })
  );
  const items = log.data?.pages.flatMap((p) => p.items) ?? [];
  const actionLabel = (a: string) => (knownActions.includes(a) ? t(`auditActions.${a}` as never) : a);

  return (
    <div className="space-y-6 p-6">
      <PageTitle
        title={t('admin.audit')}
        items={[{ label: t('admin.section') }, { label: t('admin.audit'), active: true }]}
        actions={
          <select
            className="select select-sm w-56"
            aria-label={t('admin.filterAction')}
            value={action ?? ''}
            onChange={(e) => void navigate({ search: { action: e.target.value || undefined }, replace: true })}>
            <option value="">{t('admin.all')}</option>
            {knownActions.map((a) => (
              <option key={a} value={a}>
                {actionLabel(a)}
              </option>
            ))}
          </select>
        }
      />
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>{t('admin.auditTime')}</th>
                  <th>{t('admin.auditActor')}</th>
                  <th>{t('admin.auditAction')}</th>
                  <th>{t('admin.auditTarget')}</th>
                  <th>{t('admin.auditDetails')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((e) => (
                  <tr key={e.id}>
                    <td className="text-sm whitespace-nowrap">{formatDateTime(e.at)}</td>
                    <td className="text-sm">
                      {e.actor.name ?? t('admin.system_actor')}
                      {e.actor.tokenId && <span className="badge badge-ghost badge-xs ms-1">API</span>}
                    </td>
                    <td className="text-sm">{actionLabel(e.action)}</td>
                    <td className="text-base-content/60 font-mono text-xs">{e.targetType ? `${e.targetType}:${e.targetId ?? ''}` : '–'}</td>
                    <td className="text-base-content/60 max-w-md truncate font-mono text-xs" title={JSON.stringify(e.data)}>
                      {Object.keys(e.data).length ? JSON.stringify(e.data) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {log.hasNextPage && (
        <div className="text-center">
          <button type="button" className="btn btn-sm" onClick={() => void log.fetchNextPage()} disabled={log.isFetchingNextPage}>
            {t('admin.loadMore')}
          </button>
        </div>
      )}
    </div>
  );
}
